import * as THREE from 'three';
import { gsap } from 'gsap';

export default class InteractionManager {
  constructor(scene, cameraManager, deskScene, screenManager, rendererDomElement = null, container = null) {
    this.scene = scene;
    this.cameraManager = cameraManager;
    this.deskScene = deskScene;
    this.screenManager = screenManager;
    this.rendererDomElement = rendererDomElement;
    this.container = container;
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.intersectedObject = null;
    this.isAnimating = false;
    this.lastIntersectionCheck = 0;
    this.intersectionCooldown = 100; // ms
  }

  setupMouseInteraction(container) {
    this.container = container || this.container;
    // Add event listeners to window instead of container to ensure we catch all events
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
    window.addEventListener('click', this.onClick.bind(this));
    window.addEventListener('mousedown', this.onMouseDown.bind(this));
    window.addEventListener('mouseup', this.onMouseUp.bind(this));
  }

  isPointerWithinRenderer(event) {
    const el = this.rendererDomElement;
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }

  isEventOnInteractiveUI(event) {
    // Allow "click-through" the frosted glass / section chrome, but do NOT interfere with real UI controls.
    // Keep this conservative: if something is intended to be clicked/dragged/scrolled, we don't raycast.
    const t = event.target;
    if (!t || !t.closest) return false;

    const uiSelector = [
      '.navigation',
      '.nav-item',
      '.social-links',
      '.social-link',
      '.mobile-nav-toggle',
      '.work-item',
      '.project-details-card',
      '.media-item',
      // native interactive controls
      'a',
      'button',
      'input',
      'textarea',
      'select',
      'label',
      '[role="button"]',
      '[contenteditable="true"]',
    ].join(',');

    return !!t.closest(uiSelector);
  }

  updatePointerFromEvent(event) {
    const { x01, y01 } = this.getNormalizedPointerFromEvent(event);
    this.mouse.x = x01 * 2 - 1;
    this.mouse.y = -(y01 * 2 - 1);

    // Update camera position for parallax effect
    const normalizedX = (x01 - 0.5) * 2;
    const normalizedY = (y01 - 0.5) * 2;
    this.cameraManager.handleMousePan(normalizedX, normalizedY);
  }

  getNormalizedPointerFromEvent(event) {
    const el = this.rendererDomElement || this.container;
    if (!el) {
      return {
        x01: event.clientX / window.innerWidth,
        y01: event.clientY / window.innerHeight,
      };
    }

    const rect = el.getBoundingClientRect();
    const x01 = THREE.MathUtils.clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const y01 = THREE.MathUtils.clamp((event.clientY - rect.top) / rect.height, 0, 1);
    return { x01, y01 };
  }

  onMouseMove(event) {
    if (!this.isPointerWithinRenderer(event)) return;
    // Don't interfere with UI hover/mouse behavior.
    if (this.isEventOnInteractiveUI(event)) return;
    this.updatePointerFromEvent(event);
  }

  onClick(event) {
    if (!this.isPointerWithinRenderer(event)) return;
    if (this.isEventOnInteractiveUI(event)) return;
    this.updatePointerFromEvent(event);
    this.checkIntersections();

    if (this.intersectedObject && !this.isAnimating) {
      this.handleObjectClick(this.intersectedObject);
    }
  }

  onMouseDown(event) {
    if (!this.isPointerWithinRenderer(event)) return;
    if (this.isEventOnInteractiveUI(event)) return;
    this.updatePointerFromEvent(event);
    this.checkIntersections();

    if (this.intersectedObject) {
      this.handleObjectMouseDown(this.intersectedObject);
    }
  }

  onMouseUp(event) {
    if (!this.isPointerWithinRenderer(event)) return;
    if (this.isEventOnInteractiveUI(event)) return;
    this.updatePointerFromEvent(event);
    if (this.intersectedObject) {
      this.handleObjectMouseUp(this.intersectedObject);
    }
  }

  checkIntersections() {
    const now = Date.now();
    if (now - this.lastIntersectionCheck < this.intersectionCooldown) {
      return;
    }
    this.lastIntersectionCheck = now;

    try {
      // Update the picking ray with the camera and mouse position
      this.raycaster.setFromCamera(this.mouse, this.cameraManager.getCamera());

      // Get all interactive objects
      const interactiveObjects = this.deskScene.getInteractiveObjects();
      
      // Calculate intersections
      const intersects = this.raycaster.intersectObjects(interactiveObjects, true);

      // Handle intersection changes
      if (intersects.length > 0) {
        const newIntersectedObject = intersects[0].object;
        
        if (this.intersectedObject !== newIntersectedObject) {
          if (this.intersectedObject) {
            this.handleObjectOut(this.intersectedObject);
          }
          this.intersectedObject = newIntersectedObject;
          this.handleObjectOver(this.intersectedObject);
        }
      } else {
        if (this.intersectedObject) {
          this.handleObjectOut(this.intersectedObject);
          this.intersectedObject = null;
        }
      }
    } catch (error) {
      console.error('Error checking intersections:', error);
      this.intersectedObject = null;
    }
  }

  handleObjectOver(object) {
    if (!object || this.isAnimating) return;

    gsap.to(object.scale, {
      x: 1.1,
      y: 1.1,
      z: 1.1,
      duration: 0.3,
      ease: "power2.out"
    });

    document.body.style.cursor = 'pointer';
  }

  handleObjectOut(object) {
    if (!object) return;

    gsap.to(object.scale, {
      x: 1,
      y: 1,
      z: 1,
      duration: 0.3,
      ease: "power2.out"
    });

    document.body.style.cursor = 'default';
  }

  handleObjectClick(object) {
    if (!object || this.isAnimating) return;

    this.isAnimating = true;
    this.deskScene.handleObjectAnimation(object);

    // Reset animation flag after animation completes
    setTimeout(() => {
      this.isAnimating = false;
    }, 500);
  }

  handleObjectMouseDown(object) {
    if (!object || this.isAnimating) return;

    gsap.to(object.scale, {
      x: 0.95,
      y: 0.95,
      z: 0.95,
      duration: 0.1,
      ease: "power2.out"
    });
  }

  handleObjectMouseUp(object) {
    if (!object || this.isAnimating) return;

    gsap.to(object.scale, {
      x: 1,
      y: 1,
      z: 1,
      duration: 0.1,
      ease: "power2.out"
    });
  }

  update() {
    // Always check for intersections to ensure interactions work across all sections
    this.checkIntersections();
  }
} 